import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, User, Briefcase, Eye, Download, FileText } from 'lucide-react';

const DocumentCard = ({ document }) => {
  const navigate = useNavigate();

  const getFileExtension = (fileName) => {
    if (!fileName) return 'FILE';
    return fileName.split('.').pop().toUpperCase();
  };

  const getTypeIcon = (fileName) => {
    if (!fileName) return '📄';
    const ext = fileName.toLowerCase();

    if (ext.endsWith('.pdf')) return '📄';
    if (ext.endsWith('.doc') || ext.endsWith('.docx')) return '📝';
    if (ext.endsWith('.txt')) return '📃';
    if (ext.endsWith('.xls') || ext.endsWith('.xlsx')) return '📊';
    if (ext.endsWith('.jpg') || ext.endsWith('.jpeg') || ext.endsWith('.png') || ext.endsWith('.gif')) return '🖼️';
    if (ext.endsWith('.mp4')) return '🎥';
    if (ext.endsWith('.mp3')) return '🎵';
    if (ext.endsWith('.zip') || ext.endsWith('.rar')) return '📦';
    return '📎';
  };

  const getTypeBadgeColor = (fileName) => {
    if (!fileName) return 'bg-gray-100 text-gray-700';
    const ext = fileName.toLowerCase();

    if (ext.endsWith('.pdf')) return 'bg-red-100 text-red-700';
    if (ext.endsWith('.doc') || ext.endsWith('.docx')) return 'bg-blue-100 text-blue-700';
    if (ext.endsWith('.txt')) return 'bg-gray-100 text-gray-700';
    if (ext.endsWith('.xls') || ext.endsWith('.xlsx')) return 'bg-green-100 text-green-700';
    if (ext.endsWith('.jpg') || ext.endsWith('.jpeg') || ext.endsWith('.png') || ext.endsWith('.gif')) return 'bg-purple-100 text-purple-700';
    if (ext.endsWith('.mp4')) return 'bg-pink-100 text-pink-700';
    if (ext.endsWith('.mp3')) return 'bg-orange-100 text-orange-700';
    if (ext.endsWith('.zip') || ext.endsWith('.rar')) return 'bg-yellow-100 text-yellow-700';
    return 'bg-gray-100 text-gray-700';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    if (document.fileUrl) {
      window.open(document.fileUrl, '_blank');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border border-gray-200">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="text-4xl">{getTypeIcon(document.fileName)}</div>
            <div>
              <h3 className="font-bold text-lg text-gray-800 line-clamp-2">
                {document.title}
              </h3>
              <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${getTypeBadgeColor(document.fileName)}`}>
                {getFileExtension(document.fileName)}
              </span>
            </div>
          </div>
        </div>

        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {document.description}
        </p>

        <div className="space-y-2 mb-4">
          {document.commission && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Briefcase size={16} className="text-primary-600" />
              <span className="font-medium">{document.commission.name}</span>
            </div>
          )}
          {document.archiveDate && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar size={16} className="text-primary-600" />
              <span>{new Date(document.archiveDate).toLocaleDateString('fr-FR')}</span>
            </div>
          )}
          {document.author && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <User size={16} className="text-primary-600" />
              <span>{document.author}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <FileText size={14} className="text-gray-400" />
            <span className="text-xs text-gray-500">{formatFileSize(document.fileSize)}</span>
          </div>
          <div className="flex gap-2">
            {document.fileUrl && (
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                title="Télécharger le fichier"
              >
                <Download size={16} />
              </button>
            )}
            <button
              onClick={() => navigate(`/documents/${document.id}`)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
            >
              <Eye size={16} />
              <span>Voir</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentCard;
